import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { OPS_CATEGORIES, RECURRENCES, PENDING_EXTERNAL } from "@/lib/constants";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import TaskDetailDrawer from "@/components/TaskDetailDrawer";
import { Plus, ExternalLink, RotateCw, Loader2, X } from "lucide-react";

export default function InternalOps() {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [loading, setLoading] = useState(true);
  const [drawerId, setDrawerId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/ops/tasks").then(r => setTasks(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => activeCat === "All" ? tasks : tasks.filter(t => t.ops_category === activeCat), [tasks, activeCat]);

  const counts = useMemo(() => {
    const c = { All: tasks.length };
    OPS_CATEGORIES.forEach(cat => { c[cat] = tasks.filter(t => t.ops_category === cat).length; });
    return c;
  }, [tasks]);

  return (
    <div className="space-y-6 animate-fade-in">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Phase 3</div>
          <h1 className="text-3xl font-semibold tracking-tight text-gray-900 mt-2">Internal Ops</h1>
          <p className="text-sm text-gray-500 mt-1.5">Compliance, legal, finance, hiring & infrastructure tracking.</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="create-ops-task-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded-md font-medium text-sm inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> New ops task
        </button>
      </header>

      {/* Category tabs */}
      <div className="border-b border-gray-200 flex gap-1 overflow-x-auto">
        {["All", ...OPS_CATEGORIES].map(cat => (
          <button
            key={cat}
            onClick={() => setActiveCat(cat)}
            data-testid={`ops-cat-${cat.toLowerCase().replace(/[\s&]+/g, "-")}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              activeCat === cat ? "border-navy text-navy" : "border-transparent text-gray-500 hover:text-navy"
            }`}
          >
            {cat} <span className="ml-1.5 text-[10px] font-mono text-gray-400">{counts[cat] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-400 py-10"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-200 rounded-lg p-10 text-center text-sm text-gray-400">No ops tasks in this category yet.</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/60 text-[11px] uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">Task</th>
                <th className="text-left px-3 py-2.5 font-medium">Category</th>
                <th className="text-left px-3 py-2.5 font-medium">Assignee</th>
                <th className="text-left px-3 py-2.5 font-medium">Status</th>
                <th className="text-left px-3 py-2.5 font-medium">Priority</th>
                <th className="text-left px-3 py-2.5 font-medium">Due</th>
                <th className="text-left px-3 py-2.5 font-medium">Recurs</th>
                <th className="text-left px-5 py-2.5 font-medium">Doc</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(t => (
                <tr key={t.id} data-testid={`ops-row-${t.id}`} onClick={() => setDrawerId(t.id)} className="hover:bg-gray-50 cursor-pointer">
                  <td className="px-5 py-3 font-medium text-gray-900">{t.title}</td>
                  <td className="px-3 py-3 text-gray-700">{t.ops_category || "—"}</td>
                  <td className="px-3 py-3 text-gray-700">{t.assignee_name}</td>
                  <td className="px-3 py-3"><StatusBadge status={t.status} /></td>
                  <td className="px-3 py-3"><PriorityBadge priority={t.priority} /></td>
                  <td className="px-3 py-3 text-gray-700 font-mono text-xs">{t.due_date || "—"}</td>
                  <td className="px-3 py-3 text-gray-700 text-xs">
                    {t.recurrence && t.recurrence !== "None"
                      ? <span className="inline-flex items-center gap-1 text-navy"><RotateCw className="w-3 h-3" /> {t.recurrence}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-5 py-3">
                    {t.related_doc_link ? (
                      <a href={t.related_doc_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-navy hover:underline inline-flex items-center gap-1 text-xs">
                        Open <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : <span className="text-gray-300 text-xs">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TaskDetailDrawer taskId={drawerId} onClose={() => setDrawerId(null)} onChanged={load} onEdit={() => {}} />
      {showForm && <OpsTaskForm onClose={() => setShowForm(false)} onSaved={load} />}
    </div>
  );
}

function OpsTaskForm({ onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "", ops_category: OPS_CATEGORIES[0], work_type: "Operations",
    assigned_to: "", priority: "P1", status: "Not Started",
    due_date: "", recurrence: "None", notes: "", related_doc_link: "",
    client_name: "Internal", project: "Compliance",
    format: "Not Applicable",
  });
  const [users, setUsers] = useState([]);
  const [ref, setRef] = useState({ projects: [], clients: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data));
    api.get("/reference").then(r => setRef(r.data));
  }, []);

  const filteredProjects = ref.projects.filter(p => p.client_name === form.client_name);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/tasks", form);
      onSaved && onSaved(); onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save.");
    } finally { setBusy(false); }
  };

  const allStatuses = ["Not Started", "In Progress", "In Review", "Delivered", "Approved", "Closed", "On Hold", PENDING_EXTERNAL];

  return (
    <div className="fixed inset-0 z-50 bg-gray-900/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} data-testid="ops-form-modal" className="bg-white max-w-xl w-full rounded-xl shadow-xl border border-gray-200 p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-tight">New ops task</h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-4 h-4" /></button>
        </div>
        <Field label="Title"><input required value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} data-testid="ops-title-input" className="input" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Category"><select required value={form.ops_category} onChange={(e) => setForm(f => ({ ...f, ops_category: e.target.value }))} data-testid="ops-category-select" className="input">{OPS_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></Field>
          <Field label="Recurrence"><select value={form.recurrence} onChange={(e) => setForm(f => ({ ...f, recurrence: e.target.value }))} data-testid="ops-recurrence-select" className="input">{RECURRENCES.map(r => <option key={r}>{r}</option>)}</select></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Client"><select value={form.client_name} onChange={(e) => setForm(f => ({ ...f, client_name: e.target.value, project: "" }))} className="input">{ref.clients.map(c => <option key={c.name}>{c.name}</option>)}</select></Field>
          <Field label="Project / IP"><select required value={form.project} onChange={(e) => setForm(f => ({ ...f, project: e.target.value }))} className="input"><option value="">Select…</option>{filteredProjects.map(p => <option key={p.name}>{p.name}</option>)}</select></Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Assignee"><select required value={form.assigned_to} onChange={(e) => setForm(f => ({ ...f, assigned_to: e.target.value }))} className="input"><option value="">Select…</option>{users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}</select></Field>
          <Field label="Status"><select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className="input">{allStatuses.map(s => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Priority"><select value={form.priority} onChange={(e) => setForm(f => ({ ...f, priority: e.target.value }))} className="input"><option>P0</option><option>P1</option><option>P2</option></select></Field>
        </div>
        <Field label="Due date"><input required type="date" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} data-testid="ops-due-input" className="input" /></Field>
        <Field label="Related document link"><input type="url" value={form.related_doc_link} onChange={(e) => setForm(f => ({ ...f, related_doc_link: e.target.value }))} className="input" placeholder="https://…" /></Field>
        <Field label="Notes"><textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="input" /></Field>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded">Cancel</button>
          <button type="submit" disabled={busy} data-testid="ops-save-btn" className="bg-navy hover:bg-navy-800 text-white px-4 py-2 rounded text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Create</button>
        </div>
        <style>{`.input{width:100%;background:white;border:1px solid #d1d5db;border-radius:6px;padding:8px 10px;font-size:14px;color:#111827;outline:none}.input:focus{border-color:#2B4462;box-shadow:0 0 0 3px rgba(43,68,98,.12)}`}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-gray-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
